'use client'

import { motion } from 'motion/react'
import { useInView } from 'react-intersection-observer'
import { Restaurant } from '@/payload-types'
import AnimatedText from '@/components/Antonie/Components/animated-text'
import RichText from '@/components/RichText'
import Link from 'next/link'
import { TypedLocale } from 'payload'

interface RestaurantClientProps {
  data: Restaurant
  locale: TypedLocale
}

export const RestaurantClient: React.FC<RestaurantClientProps> = ({ data, locale }) => {
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1,
  })

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2,
        delayChildren: 0.1,
      },
    },
  }

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
    },
  }

  return (
    <section className="py-12 md:py-24">
      <motion.div
        ref={ref}
        variants={containerVariants}
        initial="hidden"
        animate={inView ? 'visible' : 'hidden'}
        className="mx-auto max-w-4xl bg-white p-4 shadow-xl md:p-8"
      >
        <motion.div variants={itemVariants} className="mb-12 text-center">
          <AnimatedText
            text={data.title}
            type="h2"
            className="mb-6 font-heading text-3xl text-antonie-brown md:text-4xl"
          />
          <p className="text-lg font-light text-gray-600">{data.subtitle}</p>
        </motion.div>

        {data.content && (
          <div className="flex w-full items-center justify-center">
            <motion.div
              variants={itemVariants}
              className="flex w-full items-center justify-center space-y-2"
            >
              {data.content && (
                <RichText data={data.content} enableGutter={true} enableProse={true} />
              )}
            </motion.div>
          </div>
        )}

        <motion.div variants={itemVariants} className="mt-2 flex justify-center">
          <Link
            aria-label={locale === 'de' ? 'Jetzt Reservieren' : 'Book Now'}
            href={'mailto:info@hotel-antonie.de'}
          >
            <button 
              className="bg-antonie-brown px-8 py-3 text-lg font-light text-white transition-all duration-300 hover:bg-antonie-brown-dark"
              aria-label="Jetzt reservieren"
              title="Jetzt reservieren"
            >
              Jetzt reservieren
            </button>
          </Link>
        </motion.div>
      </motion.div>
    </section>
  )
}
